import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { REQUIRED_TEXT_ERROR } from '@components/dynamic-input-fields/src/utils';
import { Error } from '@components/error/src/error';
import { ErrorMessage } from '@components/error/src/error-message';
import { InputDate } from '@components/input-date';
import { InputTime } from '@components/input-time/src/input-time';
import { LabelPrimary } from '@components/label';
import { LabelInfo } from '@components/label/src/label-info';
import { Select, SelectOption } from '@components/select';
import { isDateLessThan } from '@utils/validations/is-date-less-than';
import { isTime } from '@utils/validations/is-time';

import { InputAccessValidityDatesProps } from './input-access-validity-dates.type';

export const InputAccessValidityDates = ({
  isEventInThePast,
  minValidFromDate,
  minValidUntilDate,
  maxEndDate,
  timezone,
  validityglobalError,
  setValidityglobalError,
}: InputAccessValidityDatesProps) => {
  const {
    register,
    formState: { errors },
    watch,
    getValues,
    setValue,
    control,
  } = useFormContext();
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const watchedAccessValidity = watch('accessValidity');

  const validateValidityDates = () => {
    const errorMessage = isDateLessThan({
      eventEndDate: maxEndDate,
      eventStartDate: minValidFromDate,
      startTime: getValues('validFromTime'),
      startDate: getValues('validFrom'),
      endTime: getValues('validUntilTime'),
      endDate: getValues('validUntil'),
      message: t('offers:validFromGreaterThanUntilTimeError'),
      messageTimeBefore: t('errors:invalidTimeErrorBefore'),
      messageTimeEnd: t('errors:invalidTimeErrorEnd'),
      timezone,
    });
    if (setValidityglobalError) setValidityglobalError(errorMessage);
  };
  return (
    <>
      <div className='flex flex-col grow col-span-2 md:col-span-1'>
        <LabelInfo htmlFor='accessValidity' text={t('offers:admissionAccessValidityTooltip')}>
          {t('offers:admissionAccessValidityLabel')}
        </LabelInfo>

        <Select
          {...register('accessValidity', {
            required: t(REQUIRED_TEXT_ERROR),
          })}
          hasError={!!errors.accessValidity}
          disabled={isEventInThePast}
          readOnly={isEventInThePast}>
          <SelectOption value='event' label={t('offers:admissionAccessValidityEventOption')} />
          <SelectOption value='limited' label={t('offers:admissionAccessValidityLimitedOption')} />
        </Select>

        <ErrorMessage name='accessValidity' />
      </div>
      {watchedAccessValidity === 'limited' && (
        <div className='mb-8'>
          <div className='mt-2 grid grid-cols-1 md:grid-cols-2 gap-6'>
            <div className='flex flex-col'>
              <LabelPrimary className='text-left' hasError={!!errors.validFrom} htmlFor='validFrom'>
                {t('offers:admissionValidityFromDateLabel')}
              </LabelPrimary>

              <div className='flex flex-row gap-3'>
                <div className='flex-auto lg:w-49.5'>
                  <Controller
                    name='validFrom'
                    control={control}
                    render={({ field }) => (
                      <InputDate
                        {...field}
                        onChange={(e) => {
                          field.onChange(e);
                          validateValidityDates();
                          setValue('validFrom', e, { shouldDirty: true });
                        }}
                        id='validFrom'
                        name='validFrom'
                        locale={locale}
                        required={false}
                        showClearDate={true}
                        hasError={!!errors.validFrom}
                        initialVisibleMonth={control._formValues.validFrom ? () => control._formValues.validFrom : null}
                        minBookingDate={minValidFromDate}
                        maxBookingDate={maxEndDate}
                        disabled={isEventInThePast}
                        readOnly={isEventInThePast}
                      />
                    )}
                  />
                  <ErrorMessage name='validFrom' />
                </div>

                <div className='shrink-0 w-24'>
                  <Controller
                    name='validFromTime'
                    control={control}
                    rules={{
                      validate: {
                        isTime: (value) =>
                          isTime({
                            value,
                            message: t('errors:invalidHourTime'),
                          }),
                      },
                    }}
                    render={({ field }) => (
                      <InputTime
                        {...field}
                        onChange={(e) => {
                          field.onChange(e);
                          validateValidityDates();
                          setValue('validFromTime', e, { shouldDirty: true });
                        }}
                        id='validFromTime'
                        name='validFromTime'
                        autoComplete='off'
                        hasError={!!errors.validFromTime}
                        disabled={isEventInThePast}
                        readOnly={isEventInThePast}
                      />
                    )}
                  />
                  <ErrorMessage name='validFromTime' />
                </div>
              </div>
            </div>

            <div className='flex flex-col'>
              <LabelPrimary className='text-left' htmlFor='validUntil' hasError={!!errors.validUntil}>
                {t('offers:admissionValidUntilLabel')}
              </LabelPrimary>

              <div className='flex flex-row gap-3'>
                <div className='flex-auto lg:w-12.375rem'>
                  <Controller
                    name='validUntil'
                    control={control}
                    render={({ field }) => (
                      <InputDate
                        {...field}
                        onChange={(e) => {
                          field.onChange(e);
                          validateValidityDates();
                          setValue('validUntil', e, { shouldDirty: true });
                        }}
                        id='validUntil'
                        name='validUntil'
                        required={false}
                        showClearDate={true}
                        locale={locale}
                        hasError={!!errors.validUntil}
                        initialVisibleMonth={
                          control._formValues.validUntil ? () => control._formValues.validUntil : null
                        }
                        minBookingDate={minValidUntilDate}
                        maxBookingDate={maxEndDate}
                        disabled={isEventInThePast}
                        readOnly={isEventInThePast}
                      />
                    )}
                  />
                  <ErrorMessage name='validUntil' />
                </div>

                <div className='shrink-0 w-24'>
                  <Controller
                    name='validUntilTime'
                    control={control}
                    rules={{
                      validate: {
                        isTime: (value) =>
                          isTime({
                            value,
                            message: t('errors:invalidHourTime'),
                          }),
                      },
                    }}
                    render={({ field }) => (
                      <InputTime
                        {...field}
                        onChange={(e) => {
                          field.onChange(e);
                          validateValidityDates();
                          setValue('validUntilTime', e, { shouldDirty: true });
                        }}
                        id='validUntilTime'
                        name='validUntilTime'
                        autoComplete='off'
                        hasError={!!errors.validUntilTime}
                        disabled={isEventInThePast}
                        readOnly={isEventInThePast}
                      />
                    )}
                  />
                  <ErrorMessage name='validUntilTime' />
                </div>
              </div>
            </div>
          </div>

          {validityglobalError && <Error>{validityglobalError}</Error>}
        </div>
      )}
    </>
  );
};
